/**
 * Cloupak 前端 API 客户端
 *
 * 封装与服务端（Hono on Cloudflare Workers）通信的 fetch 调用。
 * 包含后端 snake_case → 前端 camelCase 的字段映射和状态值转换。
 */

/** 用户信息（与后端 User 实体对应） */
export interface User {
  id: string;
  email: string;
  name: string;
  plan: string;
}

/** 应用构建状态（前端统一使用） */
export type BuildStatus = 'idle' | 'uploading' | 'building' | 'success' | 'failed';

/** 应用信息（前端统一格式） */
export interface AppInfo {
  id: string;
  name: string;
  status: BuildStatus;
  createdAt: string;
  lastBuildAt?: string | null;
  apkUrl?: string | null;
  progress?: number;
  currentStep?: string | null;
  buildLog?: string | null;
  errorMessage?: string | null;
}

/** 通用后端错误 */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

const TOKEN_STORAGE_KEY = 'cloupak_token';

/** 注入鉴权头：从 localStorage 读取 token，存在则附加 Authorization */
function buildHeaders(extra?: HeadersInit): HeadersInit {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (extra) {
    const source = extra instanceof Headers
      ? Object.fromEntries(extra.entries())
      : (extra as Record<string, string>);
    Object.assign(headers, source);
  }
  return headers;
}

/** 统一的请求封装，自动处理 JSON / 文本与错误转换 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: buildHeaders(init?.headers),
  });

  if (!res.ok) {
    let message = `请求失败（HTTP ${res.status}）`;
    try {
      const data = await res.json();
      message = data?.error ?? data?.message ?? data?.detail ?? message;
    } catch {
      // 响应体非 JSON，忽略解析错误
    }
    throw new ApiError(message, res.status);
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

// ============================================================
// 后端响应 → 前端格式映射
// ============================================================

/** 后端状态值 → 前端状态值 */
function mapStatus(backendStatus: string): BuildStatus {
  const map: Record<string, BuildStatus> = {
    draft: 'idle',
    building: 'building',
    deployed: 'success',
    success: 'success',
    failed: 'failed',
    pending: 'building',
    idle: 'idle',
    uploading: 'uploading',
  };
  return map[backendStatus] ?? 'idle';
}

/** 后端 App 原始格式（snake_case） */
interface RawApp {
  id: string;
  user_id?: string;
  name: string;
  slug?: string;
  status: string;
  r2_prefix?: string;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  last_build_at?: string | null;
  lastBuildAt?: string | null;
}

/** 将后端原始 App 对象映射为前端 AppInfo */
function mapApp(raw: RawApp): AppInfo {
  return {
    id: raw.id,
    name: raw.name,
    status: mapStatus(raw.status),
    createdAt: raw.created_at ?? raw.createdAt ?? '',
    lastBuildAt: raw.last_build_at ?? raw.lastBuildAt ?? null,
  };
}

export const api = {
  /** 注册新用户 */
  async register(email: string, password: string, name: string): Promise<{ token: string; user: User }> {
    return request<{ token: string; user: User }>('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });
  },

  /** 登录 */
  async login(email: string, password: string): Promise<{ token: string; user: User }> {
    return request<{ token: string; user: User }>('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  },

  /** 获取当前登录用户信息（需要 Authorization 头） */
  async getMe(): Promise<User> {
    const data = await request<{ user: User }>('/api/auth/me');
    return data.user;
  },

  /** 获取应用列表 */
  async getApps(): Promise<AppInfo[]> {
    const data = await request<RawApp[] | { apps: RawApp[] }>('/api/apps');
    const apps = Array.isArray(data) ? data : data.apps;
    return apps.map(mapApp);
  },

  /** 获取单个应用详情（含最近构建记录） */
  async getApp(appId: string): Promise<AppInfo> {
    const data = await request<{ app: RawApp; recentBuilds: unknown[] }>(`/api/apps/${encodeURIComponent(appId)}`);
    return mapApp(data.app);
  },

  /** 创建应用（仅建立记录，返回待上传的 appId） */
  async createApp(name: string): Promise<AppInfo> {
    const raw = await request<{ appId: string; name: string; slug: string; status: string; appToken: string; r2Prefix: string; createdAt: string }>('/api/apps', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    return {
      id: raw.appId,
      name: raw.name,
      status: mapStatus(raw.status),
      createdAt: raw.createdAt,
    };
  },

  /** 上传 ZIP 项目包（multipart/form-data，浏览器自动设置 boundary） */
  async uploadZip(appId: string, file: File): Promise<void> {
    const form = new FormData();
    form.append('file', file);
    await request<void>(`/api/apps/${encodeURIComponent(appId)}/upload`, {
      method: 'POST',
      body: form,
    });
  },

  /** 触发构建（上传完成后调用） */
  async triggerBuild(appId: string): Promise<AppInfo> {
    // 后端返回 {buildId, appId, status, message}，这里转换为 AppInfo
    const raw = await request<{ buildId: string; appId: string; status: string; message: string }>(`/api/apps/${encodeURIComponent(appId)}/build`, {
      method: 'POST',
    });
    return {
      id: raw.appId,
      name: '',
      status: mapStatus(raw.status),
      createdAt: '',
    };
  },

  /**
   * 下载 APK 文件
   * 通过 API 端点下载（需要 JWT 认证），避免依赖 *.apps.cloupak.app 域名。
   * 成功后自动触发浏览器下载。
   * @throws ApiError 如果 APK 不存在或下载失败
   */
  async downloadApk(appId: string): Promise<void> {
    const res = await fetch(`${baseUrl}/api/apps/${encodeURIComponent(appId)}/apk`, {
      headers: buildHeaders(),
    });

    if (!res.ok) {
      let message = `下载失败（HTTP ${res.status}）`;
      try {
        const data = await res.json();
        message = data?.error ?? message;
      } catch {
        // 响应体非 JSON
      }
      throw new ApiError(message, res.status);
    }

    // 将响应转为 Blob 并触发下载
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${appId}.apk`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

/**
 * 构建 APK 下载 API URL（通过 Workers API 域名，不依赖 *.apps.cloupak.app）
 */
export function buildApkUrl(appId: string): string {
  return `${baseUrl}/api/apps/${encodeURIComponent(appId)}/apk`;
}

/**
 * 拼接应用部署后的在线访问链接。
 * 默认域名为 apps.cloupak.app，可通过 VITE_APPS_DOMAIN 覆盖。
 */
export function buildAccessUrl(appId: string): string {
  const domain = import.meta.env.VITE_APPS_DOMAIN ?? 'apps.cloupak.app';
  return `https://${appId}.${domain}`;
}
